import { useCallback, useEffect, useRef } from 'react';
import type { SetURLSearchParams } from 'react-router';
import { useSearchParams } from 'react-router';
import { getStorageItem, removeStorageItem, setStorageItem, type StorageKey } from '@/libs/storage';

export interface UsePersistedSearchParamOptions {
  /** The URL query param name to read/write. */
  param: string;
  /** The localStorage key backing the last-used fallback. */
  storageKey: StorageKey;
  /** History mode for `set()`/`clear()` writes. Defaults to `'replace'`. The
   *  bootstrap restore always replaces, regardless of this setting. */
  historyMode?: 'push' | 'replace';
  /** Whether an absent param falls back to the last-used value. Defaults to
   *  `true`. `false` only skips the restore for this mount — the stored value
   *  survives for the next entry that does restore. */
  restore?: boolean;
}

export interface UsePersistedSearchParamResult {
  value: string | null;
  set: (value: string) => void;
  clear: () => void;
  /**
   * Drops the value's out-of-URL sources (bootstrap restore, stored copy)
   * WITHOUT touching the URL, for callers that remove the param themselves.
   */
  dismiss: () => void;
}

// react-router's `setSearchParams` computes its update from THIS hook
// instance's render-time snapshot, not a live re-read. If two
// usePersistedSearchParam instances (e.g. zone + location, or workspace +
// zone) both bootstrap-restore in the same mount, two independent calls in
// the same tick would each build from the same pre-bootstrap snapshot and
// clobber one another. Batching every write requested in a tick into one
// microtask-deferred `setSearchParams` call makes them compose correctly.
// Limitation: assumes a single app-wide router.
let pendingWrites: Array<(next: URLSearchParams) => void> = [];
let flushScheduled = false;

/** Queues a mount-time URL write into the tick's single history-replacing `setSearchParams`. */
export function scheduleSearchParamsWrite(
  setSearchParams: SetURLSearchParams,
  write: (next: URLSearchParams) => void,
) {
  pendingWrites.push(write);
  if (flushScheduled) return;
  flushScheduled = true;
  queueMicrotask(() => {
    const writes = pendingWrites;
    pendingWrites = [];
    flushScheduled = false;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const write of writes) write(next);
        return next;
      },
      { replace: true },
    );
  });
}

/**
 * Owns one `/map`-style URL query param with a `localStorage` fallback: on
 * mount, if the param is absent, restores the last-used value; the URL
 * always wins when present. Persists the value on every change, and drops
 * the stored value on `clear()` so a stale entity can't keep restoring
 * itself.
 *
 * Generic building block for `useActiveWorkspace` / `useMapSelection` — no
 * domain knowledge of workspaces, zones, or locations.
 */
export function usePersistedSearchParam({
  param,
  storageKey,
  historyMode = 'replace',
  restore = true,
}: UsePersistedSearchParamOptions): UsePersistedSearchParamResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = searchParams.get(param);
  const replace = historyMode === 'replace';

  // Served from the first render so the deferred restoring URL write is never
  // observable as a null -> value change (consumers reset state on one).
  const bootstrap = useRef<{ read: boolean; value: string | null }>({
    read: false,
    value: null,
  });
  if (!bootstrap.current.read) {
    bootstrap.current = {
      read: true,
      value: urlValue === null && restore ? getStorageItem<string>(storageKey) : null,
    };
  }
  const value = urlValue ?? bootstrap.current.value;

  useEffect(() => {
    if (value !== null) {
      setStorageItem(storageKey, value);
    }
  }, [value, storageKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only; reads the bootstrap restore captured during the first render
  useEffect(() => {
    if (bootstrap.current.value === null) return;

    const value = bootstrap.current.value;
    scheduleSearchParamsWrite(setSearchParams, (next) => next.set(param, value));
  }, []);

  const set = useCallback(
    (next: string) => {
      bootstrap.current.value = null;
      setSearchParams(
        (prev) => {
          const merged = new URLSearchParams(prev);
          merged.set(param, next);
          return merged;
        },
        { replace },
      );
    },
    [setSearchParams, param, replace],
  );

  const clear = useCallback(() => {
    bootstrap.current.value = null;
    removeStorageItem(storageKey);
    // Nothing to delete: skip the setter, which navigates even on a no-op write.
    if (urlValue === null) return;
    setSearchParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        merged.delete(param);
        return merged;
      },
      { replace },
    );
  }, [setSearchParams, param, replace, storageKey, urlValue]);

  const dismiss = useCallback(() => {
    bootstrap.current.value = null;
    removeStorageItem(storageKey);
  }, [storageKey]);

  return { value, set, clear, dismiss };
}
